;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/auto-clips/results/:batchId\",\"regex\":\"^/auto-clips/results/([^/]+)$\",\"paramNames\":[\"batchId\"],\"hasOptionalPrefix\":false},{\"path\":\"/dubbing/:id\",\"regex\":\"^/dubbing/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/project/:id\",\"regex\":\"^/project/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/projects/:id/analyze\",\"regex\":\"^/projects/([^/]+)/analyze$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/projects/:id/dubbing\",\"regex\":\"^/projects/([^/]+)/dubbing$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/projects/:id/investigate\",\"regex\":\"^/projects/([^/]+)/investigate$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/projects/:id/optimize\",\"regex\":\"^/projects/([^/]+)/optimize$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/projects/:id/results\",\"regex\":\"^/projects/([^/]+)/results$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/projects/:id/schedule\",\"regex\":\"^/projects/([^/]+)/schedule$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/projects/:id/upload\",\"regex\":\"^/projects/([^/]+)/upload$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/reoptimize/:videoId\",\"regex\":\"^/reoptimize/([^/]+)$\",\"paramNames\":[\"videoId\"],\"hasOptionalPrefix\":false},{\"path\":\"/thumbnails/:id/edit\",\"regex\":\"^/thumbnails/([^/]+)/edit$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/:searchPage\",\"regex\":\"^/([^/]+)$\",\"paramNames\":[\"searchPage\"],\"hasOptionalPrefix\":false},{\"path\":\"/blog/:slug\",\"regex\":\"^/blog/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/admin\"},{\"path\":\"/admin/features\"},{\"path\":\"/admin/tokens\"},{\"path\":\"/auto-clips\"},{\"path\":\"/auto-clips/select\"},{\"path\":\"/beta\"},{\"path\":\"/channel-description-generator\"},{\"path\":\"/channel-investigation\"},{\"path\":\"/channels\"},{\"path\":\"/cleaver-dubbing\"},{\"path\":\"/connect-channel\"},{\"path\":\"/dashboard\"},{\"path\":\"/diagnose\"},{\"path\":\"/dubbing\"},{\"path\":\"/history\"},{\"path\":\"/intelligence\"},{\"path\":\"/labs/cleaver-dubbing\"},{\"path\":\"/midroll-ads\"},{\"path\":\"/precision-trim\"},{\"path\":\"/precision-trim/editor\"},{\"path\":\"/projects\"},{\"path\":\"/projects/new\"},{\"path\":\"/reoptimize\"},{\"path\":\"/settings\"},{\"path\":\"/settings/about\"},{\"path\":\"/settings/accounts\"},{\"path\":\"/settings/privacy\"},{\"path\":\"/settings/profile\"},{\"path\":\"/settings/voice\"},{\"path\":\"/thumbnails\"},{\"path\":\"/translate\"},{\"path\":\"/translate/results\"},{\"path\":\"/video-ideas\"},{\"path\":\"/why\"},{\"path\":\"/dev-login\"},{\"path\":\"/extension-connect\"},{\"path\":\"/forgot-password\"},{\"path\":\"/login\"},{\"path\":\"/reset-password\"},{\"path\":\"/signup\"},{\"path\":\"/about\"},{\"path\":\"/alternatives\"},{\"path\":\"/blog\"},{\"path\":\"/changelog\"},{\"path\":\"/contact\"},{\"path\":\"/download\"},{\"path\":\"/features\"},{\"path\":\"/getting-started\"},{\"path\":\"/how-to\"},{\"path\":\"/intellectual-property\"},{\"path\":\"/pricing\"},{\"path\":\"/privacy-policy\"},{\"path\":\"/product\"},{\"path\":\"/refund-policy\"},{\"path\":\"/seeds\"},{\"path\":\"/terms-of-service\"},{\"path\":\"/unsubscribe\"}],\"isrRoutes\":[\"/:searchPage\",\"/blog/:slug\"]}";globalThis["_sentryNextJsVersion"] = "16.2.3";import * as Sentry from "@sentry/nextjs";
import { APP_ENV } from "@/lib/app-env";
import { getBuildVersion } from "@/lib/observability/build-info";
import { installChunkErrorHandler } from "@/lib/observability/chunk-error-handler";
import { installClientConsoleErrorHook } from "@/lib/observability/console-hooks";
import { installClientFetchHook } from "@/lib/observability/fetch-hooks";
import { enrichAndSanitizeSentryEvent } from "@/lib/observability/sentry-event";
import { isSentryReportingEnabled } from "@/lib/observability/sentry-env";
import {
  maybeLogStageBeforeSendDrop,
  noteClientSentryRateCapHit,
} from "@/lib/observability/sentry-pipeline-notes";

// Run BEFORE Sentry / Electron / E2E guards. Script-tag 404s for
// /_next/static/chunks/*.js during GKE rolling deploys happen outside
// React's error-boundary path, so this window-level listener is the only
// path that catches them. Safe in Electron and E2E too — the guard is
// idempotent and does nothing unless a chunk 404 or a ChunkLoadError
// rejection actually fires.
installChunkErrorHandler();

// Reduce dev tracing from 100% to 10% to curb Sentry memory overhead.
// When the dev server runs under a browser driver, PLAYWRIGHT_BASE_URL may be
// set — the client SDK picks up the same change via the lower default.
const SAMPLE_RATES: Record<typeof APP_ENV, { traces: number }> = {
  "dev-v2": { traces: 0.1 },
  "stage-v2": { traces: 0.1 },
  "prod-v2": { traces: 0.01 },
};

const rates = SAMPLE_RATES[APP_ENV];

// ── Client-side rate limiter ────────────────────────────────────────
// Caps events and transactions per minute to prevent quota exhaustion.
// Once the cap is hit, remaining events in that window are silently dropped.
const EVENT_CAP_PER_MINUTE: Record<typeof APP_ENV, number> = {
  "dev-v2": 100,
  "stage-v2": 30,
  "prod-v2": 50,
};
const TXN_CAP_PER_MINUTE: Record<typeof APP_ENV, number> = {
  "dev-v2": 200,
  "stage-v2": 40,
  "prod-v2": 30,
};

function makeRateLimiter(cap: number) {
  let count = 0;
  let windowStart = Date.now();
  return function isAllowed(): boolean {
    const now = Date.now();
    if (now - windowStart > 60_000) {
      count = 0;
      windowStart = now;
    }
    return ++count <= cap;
  };
}

const isEventAllowed = makeRateLimiter(EVENT_CAP_PER_MINUTE[APP_ENV]);
const isTxnAllowed = makeRateLimiter(TXN_CAP_PER_MINUTE[APP_ENV]);

const isElectronDesktop =
  typeof window !== "undefined" && window.electronAPI !== undefined;

// Detect E2E: NEXT_PUBLIC_E2E is set at build time for standalone E2E builds,
// baking "1" into the client bundle. PLAYWRIGHT_BASE_URL is set by the dev
// server child process but only works with Turbopack (non-NEXT_PUBLIC_ vars
// are NOT inlined in production builds).
const isE2E =
  (typeof process !== "undefined" &&
    (process.env?.NEXT_PUBLIC_E2E === "1" ||
      !!process.env?.PLAYWRIGHT_BASE_URL)) ||
  false;
const shouldInitSentry =
  isSentryReportingEnabled() && !isElectronDesktop && !isE2E;

// Skip Sentry entirely outside stage/prod, and in Electron/E2E — not just
// `enabled: false`.
// `enabled: false` still loads integrations that patch fetch, history,
// and event listeners, starving the renderer event loop in Electron.
if (shouldInitSentry) {
  Sentry.init({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
    environment: APP_ENV,
    release: getBuildVersion(),
    tracesSampleRate: rates.traces,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.5,
    maxBreadcrumbs: 50,
    sendDefaultPii: true,

    // SENTRY FIX: NODE-2F6 — Amplitude SDK fetch failures (16 events)
    // Date: 2026-04-14 | Agent: Claude Code
    // Root cause: Amplitude SDK uses its own internal fetch to
    //   api.lab.amplitude.com / *.amplitude.com. When ad blockers or mobile
    //   networks drop these, the unhandled rejection hits Sentry's default
    //   integration. The exception is a generic TypeError whose stack frames
    //   originate from amplitude SDK code — denyUrls catches these by URL.
    // Fix: denyUrls drops events whose stack frames match these patterns
    //   before beforeSend even runs, covering cases where the exception
    //   value is a generic "Failed to fetch" without amplitude in the text.
    denyUrls: [
      /amplitude\.com/i,
      /cdn\.amplitude\.com/i,
      /api\.lab\.amplitude\.com/i,
    ],

    beforeSend(event) {
      if (!isEventAllowed()) {
        noteClientSentryRateCapHit({
          addBreadcrumb: Sentry.addBreadcrumb,
          capPerMinute: EVENT_CAP_PER_MINUTE[APP_ENV],
        });
        maybeLogStageBeforeSendDrop({
          environment: APP_ENV,
          reason: "client_event_rate_cap",
        });
        return null;
      }

      // SENTRY FIX: NODE-2F4, NODE-2F6 — Drop Amplitude SDK network errors
      // Date: 2026-04-14 | Agent: Claude Code
      // Root cause: Amplitude Experiment SDK internal fetch failures to
      //   api.lab.amplitude.com bubble up as unhandled rejections. Analytics
      //   is non-critical and these pollute the error stream. denyUrls above
      //   catches stack-frame matches; this catches remaining events where
      //   "amplitude" appears in the exception value or breadcrumb URLs.
      // Fix: Drop events whose exception value mentions amplitude.com hosts,
      //   OR whose recent fetch breadcrumbs targeted amplitude endpoints.
      const exceptionValues =
        event.exception?.values?.map((v) => v.value ?? "").join(" ") ?? "";
      if (
        exceptionValues.includes("amplitude.com") ||
        exceptionValues.includes("Amplitude")
      ) {
        return null;
      }
      // Check breadcrumbs: Amplitude SDK fetch failures may produce a generic
      // TypeError with the amplitude URL only visible in the fetch breadcrumb.
      const breadcrumbs = event.breadcrumbs || [];
      const recentFetchBreadcrumbs = breadcrumbs
        .slice(-5)
        .filter((b) => b.category === "fetch" || b.category === "xhr");
      const hasAmplitudeBreadcrumb = recentFetchBreadcrumbs.some(
        (b) =>
          (typeof b.data?.url === "string" &&
            b.data.url.includes("amplitude.com")) ||
          (typeof b.message === "string" && b.message.includes("amplitude.com"))
      );
      if (
        hasAmplitudeBreadcrumb &&
        exceptionValues.includes("Failed to fetch")
      ) {
        return null;
      }
      if (hasAmplitudeBreadcrumb && exceptionValues.includes("Load failed")) {
        return null;
      }

      // SENTRY FIX: NODE-268, NODE-1ZD, NODE-1TT — Client network TypeError noise
      // Date: 2026-04-14 | Agent: Antigravity
      // Root cause: Mobile browsers (Chrome Mobile, MiuiBrowser, Safari) on
      //   unstable networks produce transient "Failed to fetch" / "Load failed"
      //   / "network error" TypeErrors. These are environmental failures
      //   (WiFi/cellular switches, ad blockers, connection drops), not code bugs.
      // Fix: Drop ALL client-side TypeError "Failed to fetch" / "Load failed" /
      //   "network error" events regardless of classification. Users retry
      //   naturally, and the errors provide zero actionable debugging value.
      const isClientNetworkTypeError =
        event.exception?.values?.some(
          (v) =>
            v.type === "TypeError" &&
            (v.value?.includes("Failed to fetch") ||
              v.value?.includes("Load failed") ||
              v.value?.includes("network error"))
        ) ?? false;
      if (isClientNetworkTypeError) {
        return null;
      }

      // SENTRY FIX: NODE-24J — EncodingError: Decoding error
      // Date: 2026-04-14 | Agent: Antigravity
      // Root cause: Browser's TextDecoder throws on corrupt/truncated video
      //   data during client-side FFmpeg processing. Not a code bug — it's
      //   broken input data from the user's file or network interruption.
      // Fix: Drop EncodingError events — they're environmental, not actionable.
      const isEncodingError =
        event.exception?.values?.some(
          (v) =>
            v.type === "EncodingError" && v.value?.includes("Decoding error")
        ) ?? false;
      if (isEncodingError) {
        return null;
      }

      // SENTRY FIX: NODE-WK — AbortError: AbortError
      // Date: 2026-04-16 | Agent: Claude Code
      // Root cause: AbortError fires from AbortController across many operations:
      //   video analysis, thumbnail generation, useNewProject hooks, navigation
      //   interruption, and tab close. These are always expected — the user
      //   intentionally navigated away, the component unmounted, or a timeout
      //   fired as designed. Despite existing per-site guards in OptimizeWorkspace
      //   and auto-clips/page.tsx, AbortError still reaches Sentry from other
      //   code paths (/login, /, thumbnail-generator-editor, etc.).
      // Fix: Global AbortError drop in beforeSend — AbortError is ALWAYS expected
      //   control flow, never a code bug. Drop it unconditionally here as the
      //   last line of defense. The tracked-fetch hook also suppresses AbortError
      //   separately (reportAbortErrors: false default), so this covers non-fetch paths.
      // DO NOT: Add to report-client-error.ts — that function already has its own
      //   AbortError check (line 65). This beforeSend catches anything that slips
      //   through or arrives via other mechanisms.
      const isAbortError =
        event.exception?.values?.some(
          (v) => v.type === "AbortError" || v.value?.includes("AbortError")
        ) ?? false;
      if (isAbortError) {
        return null;
      }

      // SENTRY FIX: VIDSEEDS-2EH — UnrecognizedActionError: Server Action "..." not found
      // Date: 2026-04-16 | Agent: Claude Code
      // Root cause: Rolling deploys change the server action ID hash. A tab that
      //   loaded before the deploy has the old hash baked into its HTML; the first
      //   form submission after the deploy throws UnrecognizedActionError from
      //   Next.js internal code BEFORE the client error boundary catches it.
      //   app/(app)/error.tsx already detects this via
      //   isRecoverableClientBoundaryError and reloads once per tab, which resolves
      //   it for the user. The Sentry report captured here is pure noise — the
      //   user is already auto-recovered by the time the report fires.
      // Fix: drop UnrecognizedActionError / stale-server-action events
      //   unconditionally. The boundary handles recovery; this just stops the
      //   noise. DO NOT remove the boundary reload path — dropping here without
      //   recovery would leave users stuck on a broken action.
      const isStaleServerAction =
        event.exception?.values?.some(
          (v) =>
            v.type === "UnrecognizedActionError" ||
            (typeof v.value === "string" &&
              /Failed to find Server Action|Could not find Server Action|Server Action.*not found|older or newer deployment/i.test(
                v.value
              ))
        ) ?? false;
      if (isStaleServerAction) {
        return null;
      }

      // SENTRY FIX: NODE-1WY — "This video track cannot be decoded by this browser"
      // Date: 2026-04-16 | Agent: Claude Code
      // Root cause: MediaBunny's internal WebCodecs VideoDecoder throws this when the
      //   browser can't decode a video codec. The client retry logic already handles
      //   this gracefully (tries SW encoder after HW encoder fails). The error
      //   propagates to Sentry as a warning-level event but is purely environmental
      //   (unsupported codec in user's video file — not a code bug).
      // Fix: Drop these events unconditionally — the UI already shows the user
      //   "Video cannot be decoded by this browser. Try H.264 MP4 format."
      // DO NOT: broaden — this is codec/browser incompatibility, handled by the
      //   user's choice of video file, not fixable in code.
      const isMediaBunnyDecodeError =
        event.exception?.values?.some(
          (v) =>
            (v.value?.includes("video track cannot be decoded") ||
              v.value?.includes("track cannot be decoded")) &&
            v.type !== "Error"
        ) ?? false;
      if (isMediaBunnyDecodeError) {
        return null;
      }

      // SENTRY FIX: VIDSEEDS-2F3, VIDSEEDS-2F2, VIDSEEDS-2F1, VIDSEEDS-20F
      // Date: 2026-04-16 | Agent: Cursor
      // Root cause: Users browsing in Brave / Safari private mode / incognito
      //   with "Block all cookies" or Chrome with site-data blocked trigger
      //   SecurityError when any code (or 3rd-party SDK we don't own) reads
      //   localStorage / IndexedDB. Our own storage reads are wrapped in
      //   try/catch; these events come from third-party SDKs (Amplitude,
      //   analytics, Stripe.js) that probe storage without catching.
      // Not actionable in our code. Users who block storage self-select into
      //   this — the app degrades gracefully (no session persistence) but
      //   the core flow still works. Dropping the noise here lets real bugs
      //   surface. If a real code path were broken, the user-visible
      //   functional error (login fails, upload breaks) would still fire.
      const isEnvironmentStorageBlock =
        event.exception?.values?.some((v) => {
          const val = v.value ?? "";
          return (
            (v.type === "SecurityError" &&
              /localStorage|sessionStorage|Access is denied for this document/i.test(
                val
              )) ||
            (v.type === "Error" &&
              /UnknownError.*user denied permission to access the database/i.test(
                val
              ))
          );
        }) ?? false;
      if (isEnvironmentStorageBlock) {
        return null;
      }

      return enrichAndSanitizeSentryEvent(event, "client");
    },
    beforeSendTransaction(event) {
      const name = event.transaction || "";
      // Drop noisy/low-value transactions that waste quota
      if (
        name.includes("/api/observability/") ||
        name.includes("/api/health/") ||
        name.includes("/_next/") ||
        name.includes("/favicon")
      ) {
        return null;
      }
      if (!isTxnAllowed()) return null;
      return event;
    },
    debug: false,
  });

  // Install as early as possible so console.error / fetch failures before
  // ErrorReportingProvider mounts still get reportClientError + Sentry context.
  installClientConsoleErrorHook();
  installClientFetchHook();

  // ── CSP Violation Listener ──────────────────────────────────────────
  // CSP violations do NOT throw JavaScript errors and are invisible to
  // Sentry's default integrations (onerror, unhandledrejection).
  // The browser only fires a SecurityPolicyViolationEvent on the document.
  // Without this listener, violations (e.g. blocked Worker scripts) cause
  // silent hangs with zero observability.
  // REGRESSION GUARD — FFmpeg CSP silent hang (2026-04-06): blob: was
  // missing from script-src, causing FFmpeg.load() to hang forever with
  // nothing reaching Sentry.
  if (typeof document !== "undefined") {
    document.addEventListener("securitypolicyviolation", (event) => {
      const detail = {
        blockedURI: event.blockedURI,
        violatedDirective: event.violatedDirective,
        effectiveDirective: event.effectiveDirective,
        originalPolicy: event.originalPolicy?.slice(0, 200),
        sourceFile: event.sourceFile,
        lineNumber: event.lineNumber,
        columnNumber: event.columnNumber,
        disposition: event.disposition,
      };

      // ── Noise filters ─────────────────────────────────────────────
      // Known benign violations that should not reach Sentry.
      const blockedURI = event.blockedURI || "";

      // Browser extensions inject resources — not our code
      if (
        blockedURI.startsWith("chrome-extension://") ||
        blockedURI.startsWith("moz-extension://") ||
        blockedURI.startsWith("safari-extension://")
      ) {
        return;
      }

      // SENTRY FIX: NODE-22V, NODE-15G — CSP eval violations are expected
      // Date: 2026-04-14 | Agent: Antigravity
      // Root cause: Production CSP intentionally excludes 'unsafe-eval'.
      //   Third-party scripts (Google Ads, analytics, browser extensions)
      //   that use eval() trigger violations. These are the desired result
      //   of the security policy working correctly — not bugs.
      // Fix: Filter eval-blocked violations from the CSP listener entirely.
      if (
        blockedURI === "eval" ||
        blockedURI.startsWith("eval:") ||
        (event.effectiveDirective === "script-src" &&
          (blockedURI === "" || blockedURI === "eval"))
      ) {
        // Additional check: if the violated directive is script-src and
        // the source is eval, this is expected CSP enforcement. Log only.
        console.info(
          `[CSP] Expected eval block filtered: ${event.effectiveDirective} → ${blockedURI || "(eval)"}`
        );
        return;
      }

      // SENTRY FIX: NODE-27E — [CSP Violation] media-src blocked data (REGRESSION #2)
      // Date: 2026-04-14 | Agent: Claude Code
      // Previous attempts:
      //   - CSP policy already includes `data:` in media-src base values
      // Root cause: Old/buggy browsers (Chrome 109 etc.) report false-positive
      //   CSP violations for data: URIs even though the enforcing policy explicitly
      //   allows them (media-src 'self' blob: data: *). These are browser bugs,
      //   not real policy violations.
      // Fix: Filter out data:/blob: URI violations when the policy already allows
      //   them — these are non-actionable false positives.
      // DO NOT: Remove data: from CSP base values thinking these are real blocks.
      if (
        (blockedURI === "data" || blockedURI.startsWith("data:")) &&
        event.originalPolicy?.includes("media-src") &&
        event.originalPolicy.includes("data:")
      ) {
        return;
      }
      if (
        (blockedURI === "blob" || blockedURI.startsWith("blob:")) &&
        event.originalPolicy?.includes("media-src") &&
        event.originalPolicy.includes("blob:")
      ) {
        return;
      }

      // Google Ads remarketing pixels from country-specific ccTLDs.
      // These fire from www.google.{ccTLD}/pagead/ — known, stable, and
      // covered by img-src * in the enforcing policy.
      const isGoogleAdsImgSrc =
        event.effectiveDirective === "img-src" &&
        /^https:\/\/www\.google\.[a-z.]+/i.test(blockedURI);

      // For report-only violations (disposition: "report"), downgrade
      // Google Ads noise to a console log instead of a Sentry event.
      if (event.disposition === "report" && isGoogleAdsImgSrc) {
        console.info(
          `[CSP Report-Only] Known noise filtered: ${event.effectiveDirective} → ${blockedURI.slice(0, 80)}`
        );
        return;
      }

      // Report-only CSP is non-blocking by definition. It frequently produces
      // noisy, non-actionable events (country-specific ad/analytics hosts,
      // filtered URIs, extension-influenced loads). Browser-native report-uri
      // telemetry already captures CSP audit data when enabled.
      if (event.disposition === "report") {
        return;
      }

      // SENTRY FIX: NODE-2GS/NODE-2G3/NODE-2GX — Distinguish FFmpeg blob worker blocks
      // Date: 2026-04-15 | Agent: Claude Code
      // Root cause: FFmpeg WASM loads via blob: URL workers. When CSP blocks blob: scripts,
      //   the error message is generic and doesn't distinguish from other CSP violations.
      // Fix: Detect blob: worker blocks (sourceFile contains ffmpeg or is empty with blob: URI)
      //   and tag them specifically so they can be correlated with FFmpeg load failures.
      const isLikelyFFmpegWorkerBlock =
        (blockedURI === "blob" || blockedURI.startsWith("blob:")) &&
        (event.effectiveDirective === "script-src" ||
          event.effectiveDirective === "worker-src" ||
          event.effectiveDirective === "child-src") &&
        (event.sourceFile?.includes("ffmpeg") ||
          !event.sourceFile ||
          event.sourceFile === "");

      console.error(
        `[CSP Violation] Directive: ${event.violatedDirective}, Blocked: ${blockedURI}${isLikelyFFmpegWorkerBlock ? " [POSSIBLE FFMPEG WORKER BLOCK]" : ""}`,
        detail
      );

      let blockedHost = blockedURI.slice(0, 60) || "(inline)";
      if (isGoogleAdsImgSrc) {
        try {
          blockedHost = new URL(blockedURI).hostname;
        } catch {
          // keep the sliced URI
        }
      }

      Sentry.captureException(
        new Error(
          isGoogleAdsImgSrc
            ? `Blocked 'image' from '${blockedHost}'`
            : isLikelyFFmpegWorkerBlock
              ? `CSP blocked blob worker (FFmpeg likely): ${event.effectiveDirective}`
              : `CSP violation: ${event.effectiveDirective} blocked ${blockedURI || "(inline)"}`
        ),
        {
          // beforeSend is errors-only — use error level + tags for triage.
          level: "error",
          tags: {
            source: "csp-violation-listener",
            cspDirective: event.effectiveDirective,
            cspBlockedURI: blockedURI.slice(0, 100) || "(inline)",
            cspDisposition: event.disposition,
            ...(isGoogleAdsImgSrc
              ? {
                  original_severity: "warning",
                  failure_classification: "degraded",
                }
              : isLikelyFFmpegWorkerBlock
                ? {
                    likely_ffmpeg_worker_block: true,
                    failure_classification: "ffmpeg_csp",
                  }
                : {}),
          },
          extra: detail,
        }
      );
    });
  }
}

export const onRouterTransitionStart =
  !isElectronDesktop && !isE2E ? Sentry.captureRouterTransitionStart : () => {};
